import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box, CircularProgress, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { getDoc, getDocs, orderBy, query, where } from 'firebase/firestore';
import SeasonBreadcrumb from '../../../components/admin/SeasonBreadcrumb';
import { useSeason } from '../../../context/SeasonContext';
import { useNotification } from '../../../context/NotificationContext';
import {
    seasonClubsCollection, seasonPlayerDoc, seasonPlayerTransfersCollection,
} from '../../../services/players';

/** yyyy-mm-dd → dd-mm-yyyy; laat andere notaties ongemoeid. */
function formatTransferDate(dateStr) {
    if (!dateStr) return '—';
    const parts = String(dateStr).split('-');
    return parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : dateStr;
}

/**
 * Echte clubtransfers uit Sportmonks voor dit seizoen. De pagina toont het
 * overzicht; het ophalen zelf gebeurt (automatisch of handmatig) via de pagina
 * Automatische taken.
 */
function AdminClubtransfers() {
    const { seasonId } = useSeason();
    const notify = useNotification();
    const [transfers, setTransfers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');

    const load = useCallback(async () => {
        if (!seasonId) return;

        setLoading(true);
        try {
            // Transfers van de laatste 100 dagen, nieuwste eerst.
            const cutoff = new Date();
            cutoff.setDate(cutoff.getDate() - 100);
            const cutoffDate = cutoff.toISOString().split('T')[0];
            const snap = await getDocs(query(
                seasonPlayerTransfersCollection(seasonId),
                where('date', '>=', cutoffDate),
                orderBy('date', 'desc'),
            ));
            const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

            // Clubs staan onder het seizoen: staat een club daar, dan doet hij mee;
            // staat hij er niet, dan is het per definitie een club buiten dit seizoen.
            const clubsSnap = await getDocs(seasonClubsCollection(seasonId));
            const clubName = new Map();
            clubsSnap.forEach((d) => clubName.set(d.id, d.data().name));
            const teamName = (teamId) => {
                if (!teamId) return '—';
                return clubName.get(String(teamId)) || 'Non-CL';
            };

            // Spelernamen: één getDoc per uniek speler-id.
            const playerIds = [...new Set(rows.map((r) => r.player_id).filter(Boolean).map(String))];
            const playerSnaps = await Promise.all(playerIds.map((id) => getDoc(seasonPlayerDoc(seasonId, id))));
            const playerName = new Map();
            playerSnaps.forEach((s, i) => {
                const id = playerIds[i];
                if (s.exists()) {
                    const p = s.data();
                    playerName.set(id, p.common_name || p.name || p.lastname || 'Onbekend');
                } else {
                    playerName.set(id, `ID: ${id}`);
                }
            });

            setTransfers(rows.map((r) => ({
                ...r,
                playerName: playerName.get(String(r.player_id)) || '—',
                fromTeam: teamName(r.from_team_id),
                toTeam: teamName(r.to_team_id),
            })));
        } catch (err) {
            console.error('Kon de transfers niet ophalen:', err);
            notify.error('De transfers van dit seizoen konden niet opgehaald worden.');
        } finally {
            setLoading(false);
        }
    }, [seasonId, notify]);

    useEffect(() => { load(); }, [load]);

    const zichtbareTransfers = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return transfers;
        return transfers.filter((t) => [t.playerName, t.fromTeam, t.toTeam]
            .some((veld) => (veld || '').toLowerCase().includes(term)));
    }, [transfers, search]);

    const nieuwste = transfers[0]?.date;

    return (
        <Box>
            <SeasonBreadcrumb page="Clubtransfers" />

            <Box sx={{ mb: 3 }}>
                <Typography variant="h4">Clubtransfers</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {transfers.length} transfers{nieuwste ? ` · nieuwste ${formatTransferDate(nieuwste)}` : ''}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                <TextField
                    size="small"
                    placeholder="Zoek speler of club"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ flex: 1, minWidth: 240 }}
                />
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : transfers.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body1" color="text.secondary">
                        Nog geen transfers voor dit seizoen in de laatste 100 dagen.
                    </Typography>
                </Paper>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 130 }}>Datum</TableCell>
                                <TableCell>Speler</TableCell>
                                <TableCell>Van</TableCell>
                                <TableCell>Naar</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {zichtbareTransfers.map((transfer) => (
                                <TableRow key={transfer.id} hover>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {formatTransferDate(transfer.date)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{transfer.playerName}</TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            color={transfer.fromTeam === 'Non-CL' ? 'text.secondary' : 'text.primary'}
                                        >
                                            {transfer.fromTeam}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="body2"
                                            color={transfer.toTeam === 'Non-CL' ? 'text.secondary' : 'text.primary'}
                                        >
                                            {transfer.toTeam}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {zichtbareTransfers.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4}>
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                                            Geen transfer gevonden voor &ldquo;{search}&rdquo;.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}

export default AdminClubtransfers;
